import { useCallback, useEffect, useState } from "react";
import {
  useParams,
  useNavigate,
  Link,
  useSearchParams,
} from "react-router-dom";
import Swal from "sweetalert2";
import { formatCurrency, formatDate, getProfileImage } from "../../../core/api";
import { deleteUser, getUser, updateUser } from "../../../core/services/userService";
import { canManageUsers } from "../../../core/adminAccess";
import ToggleSwitch from "../../../shared/ToggleSwitch";
import AppImage from "../../../shared/AppImage";
import MetricCard from "../../../shared/MetricCard";

type UserFormState = {
  name: string;
  email: string;
  phone: string;
  isActive: boolean;
};

const orderStatusTone = (status = "") => {
  if (["Delivered", "Refunded", "Return Approved"].includes(status))
    return "success";
  if (["Processing", "Shipped", "Return Requested"].includes(status))
    return "warning";
  if (["Cancelled", "Return Rejected"].includes(status)) return "danger";
  return "muted";
};

const registerFromLabel = (value = "") => {
  if (value === "web") return "Website";
  if (value === "app") return "Mobile App";
  return value || "—";
};

const shortId = (id = "") =>
  id ? `#${String(id).slice(-8).toUpperCase()}` : "—";

const UserDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [user, setUser] = useState<any>(null);
  const [stats, setStats] = useState<any>({});
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<UserFormState>({
    name: "",
    email: "",
    phone: "",
    isActive: true,
  });

  const canEditUser = canManageUsers();

  const fetchUser = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const res = await getUser(id);
      if (res.success && res.user) {
        setUser(res.user);
        setForm({
          name: res.user.name || "",
          email: res.user.email || "",
          phone: res.user.phone || "",
          isActive: res.user.isActive !== false,
        });
        setStats(res.stats || {});
        setRecentOrders(res.recentOrders || []);
        setAddresses(res.addresses || []);
        setIsEditing(false);
      } else {
        throw new Error(res.message || "User not found");
      }
    } catch (err: any) {
      Swal.fire("Error", err.message || "Failed to load user", "error");
      navigate("/users");
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    if (searchParams.get("edit") === "1" && canEditUser && user) {
      setIsEditing(true);
    }
  }, [searchParams, canEditUser, user]);

  const handleCancelEdit = () => {
    if (user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        isActive: user.isActive !== false,
      });
    }
    setIsEditing(false);
  };

  const handleSaveUser = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user || !canEditUser) return;

    setIsSaving(true);
    try {
      const res = await updateUser(user._id, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        isActive: form.isActive,
      });

      if (!res.success) throw new Error(res.message || "Unable to update user");

      await Swal.fire({
        title: "User Updated",
        text: "Customer details saved successfully.",
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });

      await fetchUser();
    } catch (err: any) {
      Swal.fire(
        "Update Failed",
        err.message || "Could not update user",
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    const confirm = await Swal.fire({
      title: `Delete ${user.name}?`,
      text: "This will permanently remove the account and all associated data.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete Account",
    });
    if (!confirm.isConfirmed) return;
    try {
      const res = await deleteUser(user._id);
      if (res.success) {
        Swal.fire({
          title: "Deleted",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        navigate("/users");
      }
    } catch (err: any) {
      Swal.fire(
        "Delete Failed",
        err.message || "Could not delete user",
        "error",
      );
    }
  };

  const handleCopyUserId = async () => {
    if (!user?._id) return;
    try {
      await navigator.clipboard.writeText(String(user._id));
      await Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "User ID copied",
        showConfirmButton: false,
        timer: 1200,
      });
    } catch {
      Swal.fire("Copy Failed", "Could not copy user ID", "error");
    }
  };

  if (isLoading) {
    return (
      <div className="page-section loading-state">
        <h2>Loading User Profile…</h2>
      </div>
    );
  }

  if (!user) return null;

  const orderBreakdown: Record<string, number> = stats.orderBreakdown || {};
  const breakdownEntries = Object.entries(orderBreakdown);
  const isActiveAccount = user.isActive !== false;

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <Link to="/users" className="btn-sm btn-outline back-link">
            ← Back to Users
          </Link>
          <div className="page-eyebrow">Customer Profile</div>
          <h1 className="page-title">{user.name}</h1>
          <div className="page-sub">
            {user.email}
            {user.phone ? ` · ${user.phone}` : ""}
            {" · "}Joined {formatDate(user.createdAt)}
          </div>
        </div>
        <div className="page-hdr-right">
          <span
            className={`status-badge ${isActiveAccount ? "success" : "danger"}`}
          >
            {isActiveAccount ? "Active" : "Inactive"}
          </span>
          {canEditUser && !isEditing && (
            <button
              type="button"
              className="btn-sm btn-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit User
            </button>
          )}
          {canEditUser && isEditing && (
            <>
              <button
                type="button"
                className="btn-sm btn-outline"
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="user-edit-form"
                className="btn-sm btn-primary"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </>
          )}
          {canEditUser && (
            <button
              type="button"
              className="btn-sm btn-danger"
              onClick={handleDelete}
            >
              Delete Account
            </button>
          )}
        </div>
      </div>

      <div className="detail-grid-2">
        <div className="table-card profile-card">
          <AppImage
            src={getProfileImage(user)}
            alt={user.name}
            crossOrigin="anonymous"
            className="profile-avatar-img"
          />

          <div>
            <div className="profile-name">{user.name}</div>
            <div className="profile-email">{user.email}</div>
          </div>

          <div className="profile-status-row">
            <span
              className={`status-badge ${isActiveAccount ? "success" : "danger"}`}
            >
              {isActiveAccount ? "Active" : "Inactive"}
            </span>
            <span className="status-badge muted">
              {registerFromLabel(user.registerFrom)}
            </span>
          </div>

          <div className="profile-meta">
            <div className="profile-meta-row">
              <span>User ID</span>
              <strong className="profile-id-value">
                {shortId(user._id)}
                <button
                  type="button"
                  className="id-copy-btn"
                  onClick={handleCopyUserId}
                >
                  Copy
                </button>
              </strong>
            </div>
            <div className="profile-meta-row">
              <span>Phone</span>
              <strong>{user.phone || "—"}</strong>
            </div>
            <div className="profile-meta-row">
              <span>Last login</span>
              <strong>
                {user.lastLoginAt ? formatDate(user.lastLoginAt) : "Never"}
              </strong>
            </div>
            <div className="profile-meta-row">
              <span>Addresses</span>
              <strong>{addresses.length} saved</strong>
            </div>
            <div className="profile-meta-row">
              <span>Wishlist</span>
              <strong>{stats.wishlistCount ?? 0} items</strong>
            </div>
          </div>
        </div>

        <div className="page-stack">
          <section className="detail-metrics-row metrics-grid">
            {[
              {
                label: "Total Orders",
                value: stats.totalOrders ?? 0,
                icon: "📦",
                tone: "blue",
                helper: "All orders placed by this customer",
              },
              {
                label: "Total Spent",
                value: formatCurrency(stats.totalSpent ?? 0),
                icon: "💰",
                tone: "green",
                helper: "Excludes cancelled and refunded",
              },
              {
                label: "Wishlist Items",
                value: stats.wishlistCount ?? 0,
                icon: "❤️",
                tone: "pink",
                helper: "Saved products",
              },
            ].map((s) => (
              <MetricCard key={s.label} metric={s} />
            ))}
          </section>

          {breakdownEntries.length > 0 && (
            <div className="table-card info-card">
              <h3 className="info-card-title">Order Status Breakdown</h3>
              <div className="status-chips">
                {breakdownEntries.map(([status, count]) => (
                  <div key={status} className="status-chip">
                    <span className={`status-badge ${orderStatusTone(status)}`}>
                      {status}
                    </span>
                    <span>{count as number}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {isEditing && canEditUser && (
        <form
          id="user-edit-form"
          onSubmit={handleSaveUser}
          className="table-card"
        >
          <div className="table-head">
            <h2>Edit Customer Details</h2>
            <p>Update name, email, phone and account status</p>
          </div>
          <div className="admin-card-body admin-form-grid">
            <div className="form-field">
              <label className="modal-label">Full Name</label>
              <input
                className="admin-input"
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                required
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Email Address</label>
              <input
                type="email"
                className="admin-input"
                value={form.email}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
                required
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Phone Number</label>
              <input
                className="admin-input"
                value={form.phone}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    phone: event.target.value,
                  }))
                }
                placeholder="Optional"
              />
            </div>
            <div className="form-field admin-status-field">
              <ToggleSwitch
                variant="row"
                label="Active account"
                hint="Customer can sign in and place orders"
                checked={form.isActive}
                onChange={(isActive) =>
                  setForm((current) => ({ ...current, isActive }))
                }
              />
            </div>
          </div>
        </form>
      )}

      <section className="table-card">
        <div className="table-head">
          <h2>Recent Orders ({recentOrders.length})</h2>
          <p>Last 5 orders placed by this customer</p>
        </div>

        {recentOrders.length === 0 ? (
          <div className="empty-state">No orders placed yet.</div>
        ) : (
          <div className="orders-table-scroll">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Items</th>
                  <th>Payment</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order: any) => (
                  <tr key={order._id}>
                    <td>
                      <Link to={`/orders/${order._id}`} className="order-code">
                        {order.orderCode ||
                          `#${String(order._id).slice(-8).toUpperCase()}`}
                      </Link>
                    </td>
                    <td>
                      {order.itemsCount} item{order.itemsCount !== 1 ? "s" : ""}
                    </td>
                    <td>
                      <span
                        className={`status-badge ${order.paymentStatus === "Completed" ? "success" : "warning"}`}
                      >
                        {order.paymentStatus}
                      </span>
                    </td>
                    <td>
                      <strong>{formatCurrency(order.totalPrice)}</strong>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${orderStatusTone(order.orderStatus)}`}
                      >
                        {order.orderStatus}
                      </span>
                    </td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>
                      <Link
                        to={`/orders/${order._id}`}
                        className="btn-sm btn-outline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="table-card">
        <div className="table-head">
          <h2>Saved Addresses ({addresses.length})</h2>
          <p>Delivery addresses saved by this customer</p>
        </div>
        {addresses.length === 0 ? (
          <div className="empty-state">No saved addresses yet.</div>
        ) : (
          <div className="address-grid">
            {addresses.map((addr: any, i: number) => (
              <div
                key={addr._id || i}
                className={`address-card ${addr.isDefault ? "is-default" : ""}`}
              >
                {addr.isDefault && (
                  <span className="address-default-badge">Default</span>
                )}
                <div className="address-label">{addr.label || "home"}</div>
                <div>{addr.fullName}</div>
                <div>{addr.address}</div>
                <div>
                  {addr.city}, {addr.state} — {addr.pinCode}
                </div>
                <div>{addr.country}</div>
                <div className="text-muted-sm">{addr.phoneNo}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default UserDetail;
